// Missing-information register (Section S).
import { useState } from "react";
import { Plus, Trash2, Zap, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Pill from "@/components/Pill";
import { inputCls } from "./shared";

const STATUS_TONE = { Open: "amber", "In progress": "blue", Resolved: "green", "Not applicable": "neutral" };
const STATUSES = ["Open", "In progress", "Resolved", "Not applicable"];

export default function MissingInfo({ rid, items = [], sections = [], canEdit, onChange }) {
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ item: "", section_code: "", why_it_matters: "", suggested_source: "", owner: "", due_date: "", interim_measure_required: false });
    const [busy, setBusy] = useState(false);

    async function add() {
        if (!form.item.trim()) { toast.error("Describe the missing item"); return; }
        setBusy(true);
        try { await api.post(`/bsc/${rid}/missing`, form); toast.success("Missing item added"); setAdding(false); setForm({ item: "", section_code: "", why_it_matters: "", suggested_source: "", owner: "", due_date: "", interim_measure_required: false }); onChange?.(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
        finally { setBusy(false); }
    }
    async function setStatus(m, status) {
        try { await api.patch(`/bsc/${rid}/missing/${m.id}`, { status }); onChange?.(); }
        catch (e) { toast.error("Failed"); }
    }
    async function createAction(m) {
        try { await api.post(`/bsc/${rid}/missing/${m.id}/create-action`, { priority: "High", assignee_name: m.owner || null }); toast.success("Action created & linked"); onChange?.(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    }
    async function remove(m) {
        if (!window.confirm("Delete this missing item?")) return;
        try { await api.delete(`/bsc/${rid}/missing/${m.id}`); onChange?.(); } catch { toast.error("Failed"); }
    }

    return (
        <div data-testid="bscr-missing-info" className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-[18px] font-bold text-[#1F1F1F]">Missing Information Register</h2>
                    <p className="text-[12.5px] text-[#6F6A64]">Open items reduce BSCR completeness until resolved or marked not applicable.</p>
                </div>
                {canEdit && <button data-testid="bscr-missing-add" onClick={() => setAdding(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}><Plus size={14} /> Add item</button>}
            </div>

            {adding && (
                <div className="rounded-xl border border-[#E5E0DA] bg-white p-4 space-y-2.5">
                    <input data-testid="bscr-missing-item" placeholder="Missing item *" value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} className={inputCls} />
                    <div className="grid sm:grid-cols-2 gap-2.5">
                        <select value={form.section_code} onChange={(e) => setForm({ ...form, section_code: e.target.value })} className={inputCls}>
                            <option value="">Section affected…</option>
                            {sections.map((s) => <option key={s.code} value={s.code}>{s.code} — {s.title}</option>)}
                        </select>
                        <input placeholder="Suggested source to check" value={form.suggested_source} onChange={(e) => setForm({ ...form, suggested_source: e.target.value })} className={inputCls} />
                        <input placeholder="Owner" value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className={inputCls} />
                        <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={inputCls} />
                    </div>
                    <input placeholder="Why it matters" value={form.why_it_matters} onChange={(e) => setForm({ ...form, why_it_matters: e.target.value })} className={inputCls} />
                    <label className="flex items-center gap-2 text-[12px]"><input type="checkbox" checked={form.interim_measure_required} onChange={(e) => setForm({ ...form, interim_measure_required: e.target.checked })} /> Interim measure required</label>
                    <div className="flex justify-end gap-2">
                        <button onClick={() => setAdding(false)} className="px-3 py-1.5 rounded-lg border border-[#E5E0DA] text-[12px] font-semibold">Cancel</button>
                        <button data-testid="bscr-missing-save" onClick={add} disabled={busy} className="px-3 py-1.5 rounded-lg text-[12px] font-semibold text-white inline-flex items-center gap-1.5" style={{ background: "var(--brand-accent,#C75A1B)" }}>{busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add</button>
                    </div>
                </div>
            )}

            {items.length === 0 ? (
                <p className="text-[13px] italic text-[#9A968F] py-6 text-center">No missing information recorded — nice.</p>
            ) : (
                <div className="space-y-2">
                    {items.map((m) => (
                        <div key={m.id} data-testid={`bscr-missing-${m.id}`} className="rounded-xl border border-[#EFEAE4] bg-white p-3">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-[13px] font-semibold text-[#1F1F1F]">{m.item}</span>
                                        {m.section_code && <span className="text-[10.5px] font-mono text-[#9A968F]">§{m.section_code}</span>}
                                        {m.interim_measure_required && <Pill tone="amber">Interim measure</Pill>}
                                        {m.linked_action_id && <Pill tone="blue">Action linked</Pill>}
                                    </div>
                                    {m.why_it_matters && <p className="text-[11.5px] text-[#6F6A64] mt-0.5">{m.why_it_matters}</p>}
                                    <div className="text-[11px] text-[#9A968F] mt-1 flex gap-3 flex-wrap">
                                        {m.owner && <span>Owner: {m.owner}</span>}
                                        {m.due_date && <span>Due: {m.due_date}</span>}
                                        {m.suggested_source && <span>Source: {m.suggested_source}</span>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    {canEdit ? (
                                        <select data-testid={`bscr-missing-status-${m.id}`} value={m.status} onChange={(e) => setStatus(m, e.target.value)} className="text-[11px] border border-[#E5E0DA] rounded-md px-1.5 py-1 bg-white">
                                            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    ) : <Pill tone={STATUS_TONE[m.status]}>{m.status}</Pill>}
                                </div>
                            </div>
                            {canEdit && (
                                <div className="flex items-center gap-3 mt-2 pt-2 border-t border-[#F4F0EB]">
                                    {!m.linked_action_id && <button data-testid={`bscr-missing-action-${m.id}`} onClick={() => createAction(m)} className="text-[11.5px] font-semibold inline-flex items-center gap-1" style={{ color: "var(--brand-accent,#C75A1B)" }}><Zap size={12} /> Create action</button>}
                                    <button onClick={() => remove(m)} className="text-[11.5px] text-[#9A968F] hover:text-[#B33A2E] inline-flex items-center gap-1"><Trash2 size={12} /> Delete</button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
